import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryCache, MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PostHogProvider, PostHogErrorBoundary } from '@posthog/react'
import posthog from 'posthog-js'
import App from './App.tsx'
import { FlowProvider } from './flow/FlowContext.tsx'
import { SessionProvider } from './session/SessionContext.tsx'
import { ErrorScreen } from './screens/ErrorScreen.tsx'
import { POSTHOG_PROJECT_TOKEN, posthogOptions } from './config/posthog.ts'
import { installDebugCapture } from './debug/errorLog.ts'
import { installAudioUnlock } from './sound.ts'
import { installUiScale } from './ui-scale.ts'
import './index.css'

// Capture runtime errors into the on-device debug buffer (rendered by DebugPanel when enabled).
installDebugCapture()

// iOS/WKWebView won't play our scan/redeem cues (fired from callbacks, not taps) until the
// document has had a user gesture — unlock the audio on the first one.
installAudioUnlock()
// Must run before first paint so the UI never flashes at design size in a smaller web view.
installUiScale()

// Server-state errors are caught by TanStack Query and turned into flow state, so they never
// reach window.onerror. Report them to PostHog here — one central seam, components stay dumb.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
  queryCache: new QueryCache({
    onError: (error, query) => posthog.captureException(error, { source: 'query', queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      posthog.captureException(error, { source: 'mutation', mutationKey: mutation.options.mutationKey }),
  }),
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element #root not found')

createRoot(root).render(
  <StrictMode>
    <PostHogProvider apiKey={POSTHOG_PROJECT_TOKEN} options={posthogOptions}>
      <PostHogErrorBoundary fallback={<ErrorScreen message="An unexpected error occurred. Please try again." />}>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <FlowProvider>
              <App />
            </FlowProvider>
          </SessionProvider>
        </QueryClientProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </StrictMode>,
)
